import React, { useState, useEffect, useCallback } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { Button, Typography, Paper, Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3),
    margin: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
  result: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  error: {
    color: theme.palette.error.main,
  },
  success: {
    color: theme.palette.success.main,
  },
}));

export const SourcebotTestComponent = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  const testSourcebotAPI = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      console.log('[Sourcebot Test] Fetching repos from /api/sourcebot-backend/list-repos');
      const { token } = await identityApi.getCredentials();
      const response = await fetch('/api/sourcebot-backend/list-repos', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[Sourcebot Test] Success:', data);
      setResult(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[Sourcebot Test] Error:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-test on component mount
  useEffect(() => {
    testSourcebotAPI();
  }, [testSourcebotAPI]);

  return (
    <Paper className={classes.paper}>
      <Typography variant="h5" gutterBottom>
        Sourcebot MCP API Test
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Testing: <code>/api/sourcebot-backend/list-repos</code>
      </Typography>

      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={testSourcebotAPI}
          disabled={loading}
          className={classes.button}
        >
          {loading ? <CircularProgress size={20} /> : 'Test API'}
        </Button>
      </Box>

      {error && (
        <Box className={classes.result}>
          <Typography variant="h6" className={classes.error}>
            Error:
          </Typography>
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        </Box>
      )}

      {result && (
        <Box className={classes.result}>
          <Typography variant="h6" className={classes.success}>
            Success! Response:
          </Typography>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Box>
      )}

      {loading && (
        <Box className={classes.result}>
          <Typography variant="body2">
            Loading... (Check browser console for details)
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
